/* eslint-disable jira/import-whitelist */
import { loadQuery, type PreloadedQuery } from 'react-relay';
import { isProjectsInJiraM2Enabled } from '@jira/platform__services-native-integration-projects/src/index.tsx';
import {
	isCustomFilter,
	isFilterId,
} from '@jira/issue-navigator__actions-common/src/utils/filters/index.tsx';
import { buildMainIssueNavigatorQueryVariables } from '@jira/issue-navigator__issue-navigator-query-variables/src/buildMainIssueNavigatorQueryVariables.tsx';
import {
	getHydrateJqlInput,
	getIssueSearchInput,
	getParams,
} from '@jira/issue-navigator__issue-navigator-query-variables/src/getIssueSearchInput.tsx';
import {
	getIssueSearchResourceKey,
	getResourceKey,
} from '@jira/issue-navigator__issue-navigator-query-variables/src/getIssueSearchResourceKey.tsx';
import { isChromelessRoute } from '@jira/issue-navigator__issue-navigator-query-variables/src/isChromelessRoute.tsx';
import {
	getMaxIssuesPerPage,
	ISSUE_SEARCH_NAMESPACE,
	MAX_AMOUNT_OF_COLUMNS,
	NATURAL_LANGUAGE_TO_JQL,
	NIN_GLOBAL_OPERATION_SCOPE,
	NIN_PROJECT_OPERATION_SCOPE,
} from '@jira/issue-navigator__issue-navigator/src/common/constants.tsx';
import { convertFilterIdToIssueNavigatorId } from '@jira/issue-navigator__issue-navigator/src/common/utils/index.tsx';
import { getOperationScope } from '@jira/issue-table__issue-table-common/src/utils/index.tsx';
import { createAri } from '@jira/platform__utils-ari/src/index.tsx';
import getRelayEnvironment from '@jira/platform__graphql-relay-environment/src/index.tsx';
import { RELAY_RESOURCE_TYPE } from '@jira/platform__graphql-relay-utils/src/services/resources/index.tsx';
import { createViewAri } from '@jira/issue-navigator__view-ari/src/index.tsx';
import JQLBuilderWithAIFilterRefinementQuery, {
	type JQLBuilderWithAIFilterRefinementQuery as JQLBuilderWithAIFilterRefinementQueryType,
} from '@jira/jql-builder__with-ai/src/ui/filters/filter-refinement/__generated__/JQLBuilderWithAIFilterRefinementQuery.graphql';
import FilterRefinementWithAiQuery, {
	type FilterRefinementWithAiQuery as FilterRefinementWithAiQueryType,
	type FilterRefinementWithAiQuery$variables,
} from '@jira/filter-refinement__filter-refinement-with-ai/src/__generated__/FilterRefinementWithAiQuery.graphql';
import HydrationRelayQuery, {
	type useHydrationQueryLoaderQuery as HydrationRelayQueryType,
	type useHydrationQueryLoaderQuery$variables,
} from '@jira/jql-builder__common/src/ui/hydrate-jql-builder/__generated__/useHydrationQueryLoaderQuery.graphql';
import HeaderFilterRelayQuery, {
	type main_FilterQuery as FilterRelayQueryType,
} from '@jira/issue-navigator__issue-navigator/src/__generated__/main_FilterQuery.graphql';
import IssueNavigatorRelayQuery, {
	type JiraAtlassianIntelligenceFeatureEnum,
	type main_IssueNavigatorQuery as IssueNavigatorRelayQueryType,
	type main_IssueNavigatorQuery$variables,
	type JiraIssueSearchOperationScope,
	type JiraIssueSearchScope,
} from '@jira/issue-navigator__issue-navigator/src/__generated__/main_IssueNavigatorQuery.graphql';
import { fg } from '@atlassian/jira-feature-gating';
import {
	createResource,
	type RouterContext,
	type RouteResource,
} from '@atlassian/react-resource-router';
import { QueryPromisesMap } from '@atlassian/relay-query-promises';
import { expVal } from '@atlassian/jira-feature-experiments';
import {
	RESOURCE_TYPE_ISSUE_NAVIGATOR_RELAY,
	RESOURCE_TYPE_ISSUE_NAVIGATOR_FILTER_RELAY,
	RESOURCE_TYPE_ISSUE_NAVIGATOR_HYDRATION_RELAY,
	RESOURCE_TYPE_ISSUE_NAVIGATOR_FILTER_REFINEMENT_RELAY,
	RESOURCE_TYPE_ISSUE_NAVIGATOR_FILTER_REFINEMENT_WITH_AI_RELAY,
} from './sortDirections.constants.tsx';

const getFilterResourceKey = (baseKey: string, { query: { filter } }: RouterContext) => {
	const filterId = Boolean(filter) && isFilterId(filter) ? filter : null;
	return filterId ? `${baseKey}-${filterId}` : baseKey;
};

const loadIssueNavigatorRelayQuery = async (variables: main_IssueNavigatorQuery$variables) => {
	const queryReference = loadQuery<IssueNavigatorRelayQueryType>(
		getRelayEnvironment(),
		IssueNavigatorRelayQuery,
		variables,
		{
			fetchPolicy: 'network-only',
			networkCacheConfig: {
				metadata: { META_SLOW_ENDPOINT: true },
			},
		},
	);

	if (__SERVER__ && queryReference.id != null) {
		await QueryPromisesMap.get(queryReference.id);
	}

	return queryReference;
};

/**
 * We are using `createResource` instead of `createRelayResource` for two main reasons:
 * 1. The `getKey` provided by `createRelayResource` triggers this resource to reload every time a URL parameters change
 *    causes the query variables to change, preventing us from using our own custom reload hooks that are further down
 *    in Issue Navigator. This results in a full-page loading state any time one of the page params changes.
 * 2. Disabling this resource for SSR+Anonymous users (see comment below in `getData`)
 */
export const issueNavigatorRelayResource: RouteResource<
	PreloadedQuery<IssueNavigatorRelayQueryType, Record<string, unknown>>
> = createResource<PreloadedQuery<IssueNavigatorRelayQueryType>>({
	type: `${RELAY_RESOURCE_TYPE}_${RESOURCE_TYPE_ISSUE_NAVIGATOR_RELAY}`,
	getKey: getIssueSearchResourceKey,
	// @ts-expect-error - might return null
	getData: async (routerContext, { tenantContext }) => {
		// SSR/AGG does not currently work for anonymous users and returns a 403. We should enable anonymous SSR once
		// the underlying auth issues have been fixed. See more details here https://hello.atlassian.net/wiki/spaces/~hkim/pages/1853841608/Why+are+Jira+SSR+requests+to+AGG+not+working+for+anonymous+users
		if (__SERVER__ && tenantContext.isAnonymous) {
			return null;
		}
		if (
			fg('jira_nin_main_query_variables_builder') ||
			fg('jira_nin_force_client_only_main_query')
		) {
			return loadIssueNavigatorRelayQuery(
				buildMainIssueNavigatorQueryVariables(routerContext, tenantContext),
			);
		}
		// Legacy main_IssueNavigatorQuery variables path. Delete this block when
		// `jira_nin_main_query_variables_builder` is cleaned up.
		const {
			projectKey,
			issueKey,
			jql: jqlParam,
			defaultJql,
			filter,
			groupBy,
			hideDone,
		} = getParams(routerContext);
		const issueSearchInput = getIssueSearchInput(defaultJql, jqlParam, filter, projectKey);
		const maxIssuesPerPage = getMaxIssuesPerPage();
		const chromelessRoute = isChromelessRoute(routerContext);
		let viewId;
		let isIFCEnabled = Boolean(projectKey);
		const filterId = Boolean(filter) && isCustomFilter(filter) ? filter : null;
		if (chromelessRoute) {
			// Disable IFC for chromeless (i.e. embed) routes
			isIFCEnabled = false;
			// Explicitly load list view for chromeless (i.e. embed) routes
			viewId = filterId ? convertFilterIdToIssueNavigatorId(filterId) : 'list_default';
		}
		let staticViewInput;
		if (projectKey === undefined || projectKey === null) {
			staticViewInput = {
				isHierarchyEnabled: false,
				isGroupingEnabled: false,
				isHideDoneEnabled: false,
			};
		} else if (hideDone != null) {
			staticViewInput = { isHideDoneEnabled: hideDone };
		}
		const viewConfigInput = staticViewInput
			? { staticViewInput }
			: { viewInput: { namespace: ISSUE_SEARCH_NAMESPACE } };
		let jiraViewQueryInput: main_IssueNavigatorQuery$variables['jiraViewQueryInput'] = null;
		const itemIdSlug = routerContext.match.params.navigationItemId;
		const itemId = itemIdSlug ? `list/${itemIdSlug}` : 'list';
		if (isProjectsInJiraM2Enabled()) {
			const subspaceId = routerContext.match.params.subspaceId;
			if (subspaceId) {
				// Subcontainer (subspace) scoped view
				jiraViewQueryInput = {
					subcontainerIdAndItemQuery: {
						cloudId: tenantContext.cloudId,
						itemId,
						subcontainerId: subspaceId,
					},
				};
			} else if (projectKey == null) {
				jiraViewQueryInput = {
					viewId: createViewAri({
						activationId: tenantContext.activationId,
						cloudId: tenantContext.cloudId,
					}),
				};
			} else {
				jiraViewQueryInput = {
					projectKeyAndItemQuery: {
						cloudId: tenantContext.cloudId,
						projectKey,
						itemId,
					},
				};
			}
		} else {
			jiraViewQueryInput =
				projectKey == null
					? {
							viewId: createViewAri({
								activationId: tenantContext.activationId,
								cloudId: tenantContext.cloudId,
							}),
						}
					: {
							projectKeyAndItemQuery: {
								cloudId: tenantContext.cloudId,
								projectKey,
								itemId,
							},
						};
		}
		let operationScope: JiraIssueSearchOperationScope;
		if (isIFCEnabled && expVal('jira_inline_field_config', 'isInlineFieldConfigEnabled', false)) {
			operationScope = getOperationScope(projectKey);
		} else if (projectKey) {
			operationScope = NIN_PROJECT_OPERATION_SCOPE;
		} else {
			operationScope = NIN_GLOBAL_OPERATION_SCOPE;
		}
		const scope: JiraIssueSearchScope = {
			operationScope,
			projectKey: projectKey ?? null,
		};
		const fieldSetsScope = {
			operationScope,
			projectKey: projectKey ?? null,
		};
		const dynamicSearchKey = getIssueSearchResourceKey(routerContext);
		const legacyVariables: main_IssueNavigatorQuery$variables = {
			cloudId: tenantContext.cloudId,
			first: issueKey == null ? maxIssuesPerPage : null,
			issueSearchInput,
			namespace: ISSUE_SEARCH_NAMESPACE,
			fieldSetsInput: {
				viewInput: {
					namespace: ISSUE_SEARCH_NAMESPACE,
					filterId,
					viewId,
				},
			},
			after: null,
			options: { issueKey },
			filterId,
			amountOfColumns: MAX_AMOUNT_OF_COLUMNS,
			shouldQueryHasChildrenWithCount: projectKey !== null && projectKey !== undefined,
			scope,
			viewConfigInput,
			staticViewInput,
			jiraViewQueryInput,
			settings: {
				groupBy,
				hideDone,
				jql: 'jql' in issueSearchInput ? issueSearchInput.jql : undefined,
			},
			atlassianIntelligenceProductFeature:
				// oxlint-disable-next-line typescript/consistent-type-assertions
				NATURAL_LANGUAGE_TO_JQL as JiraAtlassianIntelligenceFeatureEnum,
			projectKey: projectKey ?? '',
			shouldQueryProject: Boolean(projectKey),
			shouldQueryConditionalFormatting: Boolean(projectKey),
			shouldQueryConditionalFormattingFromSavedView: Boolean(projectKey),
			shouldQuerySaveResetViewSettingsButton:
				!tenantContext.isAnonymous && expVal('saved_list_view', 'isEnabled', false),
			groupBy: groupBy ?? null,
			shouldQueryRankField:
				Boolean(projectKey) && expVal('pnin_jql_order_by_rank_enabled', 'isEnabled', false),
			fieldSetsScope,
			viewId,
			/*
			This key is used to generate a unique identity for the issue connection record in the Relay store when running a
			new search. This is to prevent concurrency related bugs occurring when performing a refetch that returns a new
			connection of results. Ordinarily this would be handled automatically when relevant query variables change (as
			these are used to form the connection key), however we override the default connection filters which allows us
			to modify variables during pagination operations without producing a new connection key.
			 */
			dynamicSearchKey,
		};
		return loadIssueNavigatorRelayQuery(legacyVariables);
	},
	maxAge: 0,
});

/**
 * We are using `createResource` instead of `createRelayResource` for two main reasons:
 * 1. The `getKey` provided by `createRelayResource` triggers this resource to reload every time a URL parameters change
 *    causes the query variables to change, preventing us from using our own custom reload hooks that are further down
 *    in Issue Navigator. This results in a full-page loading state any time one of the page params changes.
 * 2. Disabling this resource for SSR+Anonymous users (see comment below in `getData`)
 */
export const issueNavigatorFilterRelayResource: RouteResource<
	PreloadedQuery<FilterRelayQueryType, Record<string, unknown>>
> = createResource<PreloadedQuery<FilterRelayQueryType>>({
	type: `${RELAY_RESOURCE_TYPE}_${RESOURCE_TYPE_ISSUE_NAVIGATOR_FILTER_RELAY}`,
	getKey: (routerContext: RouterContext) =>
		getFilterResourceKey('issue-navigator-filter-relay', routerContext),
	// @ts-expect-error - might return null
	getData: async (routerContext, { tenantContext }) => {
		// SSR/AGG does not currently work for anonymous users and returns a 403. We should enable anonymous SSR once
		// the underlying auth issues have been fixed. See more details here https://hello.atlassian.net/wiki/spaces/~hkim/pages/1853841608/Why+are+Jira+SSR+requests+to+AGG+not+working+for+anonymous+users
		if (__SERVER__ && tenantContext.isAnonymous) {
			return null;
		}
		const { filter } = getParams(routerContext);
		const includeFilter = Boolean(filter) && isFilterId(filter);
		const variables = {
			includeFilter,
			filterAri: includeFilter
				? createAri({
						resourceOwner: 'jira',
						cloudId: tenantContext.cloudId,
						resourceType: 'filter',
						resourceId: filter,
						activationId: tenantContext.activationId,
					})
				: '',
		};
		const options = { fetchPolicy: includeFilter ? 'network-only' : 'store-only' } as const;
		const queryReference = loadQuery(
			getRelayEnvironment(),
			HeaderFilterRelayQuery,
			variables,
			options,
		);
		if (__SERVER__ && queryReference.id != null) {
			await QueryPromisesMap.get(queryReference.id);
		}
		return queryReference;
	},
	maxAge: 0,
});

/**
 * We are using `createResource` instead of `createRelayResource` for two main reasons:
 * 1. The `getKey` provided by `createRelayResource` triggers this resource to reload every time a URL parameters change
 *    causes the query variables to change, preventing us from using our own custom reload hooks that are further down
 *    in Issue Navigator. This results in a full-page loading state any time one of the page params changes.
 * 2. Disabling this resource for SSR+Anonymous users (see comment below in `getData`)
 */
export const issueNavigatorHydrationRelayResource: RouteResource<
	PreloadedQuery<HydrationRelayQueryType, Record<string, unknown>>
> = createResource<PreloadedQuery<HydrationRelayQueryType>>({
	type: `${RELAY_RESOURCE_TYPE}_${RESOURCE_TYPE_ISSUE_NAVIGATOR_HYDRATION_RELAY}`,
	getKey: (routerContext: RouterContext) =>
		getResourceKey('issue-navigator-hydration-relay', routerContext),
	// @ts-expect-error - might return null
	getData: async (routerContext, { tenantContext }) => {
		// SSR/AGG does not currently work for anonymous users and returns a 403. We should enable anonymous SSR once
		// the underlying auth issues have been fixed. See more details here https://hello.atlassian.net/wiki/spaces/~hkim/pages/1853841608/Why+are+Jira+SSR+requests+to+AGG+not+working+for+anonymous+users
		if (__SERVER__ && tenantContext.isAnonymous) {
			return null;
		}
		const { projectKey, jql: jqlParam, defaultJql, filter } = getParams(routerContext);
		const isProjectNIN = Boolean(projectKey);
		if (isProjectNIN) {
			return null;
		}
		const itemIdSlug = routerContext.match.params.navigationItemId;
		const itemId = itemIdSlug ? `list/${itemIdSlug}` : 'list';
		const hydrateJqlInput = getHydrateJqlInput({
			activationId: tenantContext.activationId,
			cloudId: tenantContext.cloudId,
			defaultJql,
			jqlParam,
			filter,
			projectKey,
			itemId,
		});
		const variables: useHydrationQueryLoaderQuery$variables = {
			accountId: tenantContext.atlassianAccountId ?? '',
			cloudId: tenantContext.cloudId,
			includeUser: true,
			includeJira: true,
			viewContext: null,
			input: hydrateJqlInput,
			scope:
				!isProjectNIN &&
				(fg('projects_in_jira_ga_drop') ||
					expVal('anip-1095-goals-in-harmonised-filter', 'isEnabled', false))
					? { list: { operationScope: NIN_GLOBAL_OPERATION_SCOPE } }
					: null,
		};
		const queryReference = loadQuery<HydrationRelayQueryType>(
			getRelayEnvironment(),
			HydrationRelayQuery,
			variables,
			{
				fetchPolicy: 'network-only',
				networkCacheConfig: {
					metadata: { META_SLOW_ENDPOINT: true },
				},
			},
		);
		if (__SERVER__ && queryReference.id != null) {
			await QueryPromisesMap.get(queryReference.id);
		}
		return queryReference;
	},
	maxAge: 0,
});

export const issueNavigatorFilterRefinementRelayResource: RouteResource<
	PreloadedQuery<JQLBuilderWithAIFilterRefinementQueryType, Record<string, unknown>>
> = createResource<PreloadedQuery<JQLBuilderWithAIFilterRefinementQueryType>>({
	type: `${RELAY_RESOURCE_TYPE}_${RESOURCE_TYPE_ISSUE_NAVIGATOR_FILTER_REFINEMENT_RELAY}`,
	getKey: () => 'issue-navigator-filter-refinement-relay',
	// @ts-expect-error - might return null
	getData: async (routerContext, { tenantContext }) => {
		// SSR/AGG does not currently work for anonymous users and returns a 403. We should enable anonymous SSR once
		// the underlying auth issues have been fixed. See more details here https://hello.atlassian.net/wiki/spaces/~hkim/pages/1853841608/Why+are+Jira+SSR+requests+to+AGG+not+working+for+anonymous users
		if (__SERVER__ && tenantContext.isAnonymous) {
			return null;
		}
		// When the `nl2jql_filter_refinement_ui` experiment is enabled the filter refinement UI is
		// served by `issueNavigatorFilterRefinementWithAiRelayResource` instead. Bail out here to
		// avoid preloading (and paying for) both queries on the same route.
		if (expVal('nl2jql_filter_refinement_ui', 'isEnabled', false)) {
			return null;
		}
		const { projectKey } = getParams(routerContext);
		const isProjectNIN = Boolean(projectKey);
		if (!isProjectNIN) {
			return null;
		}
		const variables = {
			cloudId: tenantContext.cloudId,
			isFilterRefinementEnabled: true,
			scope: {
				project: {
					projectKey,
				},
			},
		};
		const queryReference = loadQuery<JQLBuilderWithAIFilterRefinementQueryType>(
			getRelayEnvironment(),
			JQLBuilderWithAIFilterRefinementQuery,
			variables,
			{
				fetchPolicy: 'network-only',
				networkCacheConfig: {
					metadata: { META_SLOW_ENDPOINT: true },
				},
			},
		);
		if (__SERVER__ && queryReference.id != null) {
			await QueryPromisesMap.get(queryReference.id);
		}
		return queryReference;
	},
	maxAge: 0,
});

/**
 * Preloads the filter refinement query owned by `@jira/filter-refinement__filter-refinement-with-ai`
 * for the `nl2jql_filter_refinement_ui` experiment. Loading is conditional on:
 *   - the `nl2jql_filter_refinement_ui` experiment being enabled, and
 *   - the current scope being Project NIN (i.e. a project key is present in the route).
 * Otherwise `null` is returned and the FilterRefinementWithAi component is not rendered.
 */
export const issueNavigatorFilterRefinementWithAiRelayResource: RouteResource<
	PreloadedQuery<FilterRefinementWithAiQueryType, Record<string, unknown>>
> = createResource<PreloadedQuery<FilterRefinementWithAiQueryType>>({
	type: `${RELAY_RESOURCE_TYPE}_${RESOURCE_TYPE_ISSUE_NAVIGATOR_FILTER_REFINEMENT_WITH_AI_RELAY}`,
	getKey: () => 'issue-navigator-filter-refinement-with-ai-relay',
	// @ts-expect-error - might return null
	getData: async (routerContext, { tenantContext }) => {
		if (!expVal('nl2jql_filter_refinement_ui', 'isEnabled', false)) {
			return null;
		}
		const { projectKey } = getParams(routerContext);
		const isProjectNIN = Boolean(projectKey);
		if (!isProjectNIN) {
			return null;
		}
		const variables: FilterRefinementWithAiQuery$variables = {
			cloudId: tenantContext.cloudId,
			scope: {
				project: {
					projectKey,
				},
			},
			atlassianIntelligenceProductFeature: NATURAL_LANGUAGE_TO_JQL,
		};
		const queryReference = loadQuery<FilterRefinementWithAiQueryType>(
			getRelayEnvironment(),
			FilterRefinementWithAiQuery,
			variables,
			{
				fetchPolicy: 'network-only',
				networkCacheConfig: {
					metadata: { META_SLOW_ENDPOINT: true },
				},
			},
		);
		// SSR-safe: QueryPromisesMap only yields a promise on the server (where the query is
		// registered); on the client this is a no-op, so we can await unconditionally without a
		// server/client render check.
		const queryPromise =
			queryReference.id != null ? QueryPromisesMap.get(queryReference.id) : undefined;
		if (queryPromise != null) {
			await queryPromise;
		}
		return queryReference;
	},
	maxAge: 0,
});
